import React from "react";
import { ArrowUpRight, ArrowDownRight, Clock, Lightning } from "@phosphor-icons/react";
import { cn, formatBigCount } from "@/lib/format";
import { Panel, MiniBars, windowDelta, avgDelta, Freshness } from "@/components/assets/parts";

const Insight = ({ up, title, body, bars, barColor, testid }) => {
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <div data-testid={testid} className="flex flex-1 items-center gap-3 px-5 py-3.5 border-b border-primary/10 last:border-b-0">
      <span className={cn("mt-0.5 inline-flex items-center justify-center h-8 w-8 rounded-lg shrink-0", up ? "bg-emerald-400/10 text-emerald-400" : "bg-rose-400/10 text-rose-400")}>
        <Icon size={16} weight="bold" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-foreground">{title}</div>
        <div className="text-[11px] text-muted-foreground/80 leading-snug mt-0.5">{body}</div>
      </div>
      <span className="shrink-0 self-center"><MiniBars values={bars} color={barColor} /></span>
    </div>
  );
};

export const WhatChanged = ({ daily = [], freshness }) => {
  const txD = windowDelta(daily, "tx_count", 7);
  const activeD = avgDelta(daily, "active_assets", 7);
  const txBars = (daily || []).slice(-10).map((p) => p.tx_count);
  const activeBars = (daily || []).slice(-10).map((p) => p.active_assets);
  const lag = freshness?.lag_blocks;
  const ageMin = freshness?.data_age_seconds != null ? Math.round(freshness.data_age_seconds / 60) : null;

  return (
    <Panel title="What changed?" icon={Lightning} right={<Freshness freshness={freshness} testid="whatchanged-freshness" />} testid="what-changed" className="h-full flex flex-col">
      <div className="flex-1 flex flex-col">
      {txD ? (
        <Insight
          testid="insight-activity"
          up={(txD.pct ?? 0) >= 0}
          title={`Asset activity ${(txD.pct ?? 0) >= 0 ? "up" : "down"}`}
          body={<>Transactions are {(txD.pct ?? 0) >= 0 ? "+" : ""}{txD.pct != null ? txD.pct.toFixed(1) : "0"}% vs the previous 7 days ({formatBigCount(txD.prev)} → {formatBigCount(txD.cur)}).</>}
          bars={txBars}
          barColor="#22d3ee"
        />
      ) : null}
      {activeD ? (
        <Insight
          testid="insight-active"
          up={(activeD.pct ?? 0) >= 0}
          title={`${(activeD.pct ?? 0) >= 0 ? "More" : "Fewer"} active assets`}
          body={<>Daily active assets are {(activeD.pct ?? 0) >= 0 ? "+" : ""}{activeD.pct != null ? activeD.pct.toFixed(1) : "0"}% vs the previous 7 days ({formatBigCount(activeD.prev)} → {formatBigCount(activeD.cur)} / day).</>}
          bars={activeBars}
          barColor="#a855f7"
        />
      ) : null}
      {!txD && !activeD ? (
        <div className="px-5 py-8 text-center text-[12px] text-muted-foreground mono animate-pulse">computing insights…</div>
      ) : null}
      <div data-testid="insight-lag" className={cn("flex flex-1 items-center gap-3 px-5 py-3.5", freshness?.stale ? "bg-amber-400/5" : "")}>
        <span className="mt-0.5 inline-flex items-center justify-center h-8 w-8 rounded-lg shrink-0 bg-amber-400/10 text-amber-300">
          <Clock size={16} weight="duotone" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-foreground">{freshness?.stale ? "Data is stale" : "Data freshness"}</div>
          <div className="text-[11px] text-muted-foreground/80 leading-snug mt-0.5">
            {freshness
              ? <>Analytics checkpoint is {ageMin != null ? `~${ageMin}m` : "a few blocks"} behind the chain tip{lag != null ? ` (lag ${lag} blocks)` : ""}. {freshness.stale ? "The indexer may have stalled." : "Chain data is up to date."}</>
              : "Waiting for freshness signal…"}
          </div>
        </div>
      </div>
      </div>
    </Panel>
  );
};
